// Identifier quelqu'un dans un texte : « @Grâce ».
//
// Deux moitiés qui doivent rester d'accord :
//   · `useMentions` équipe un champ de saisie — taper @ propose des membres ;
//   · `TexteRiche` transforme ensuite « @Grâce Adjovi » en lien vers son profil.
//
// La liste des personnes citées est enregistrée AVEC le texte (`mentions`), et
// c'est elle qui fait foi. On ne devine jamais un nom depuis le texte seul : un
// membre peut écrire « @Grâce » sans avoir choisi personne, et deux membres
// peuvent porter le même nom. Sans cette liste, on lierait vers le mauvais
// profil — ou pire, on préviendrait quelqu'un qui n'a rien à voir.

import { useRef, useState } from "react";
import Store from "../store.js";
import Avatar from "./Avatar.jsx";

/** Le morceau de texte qu'on est en train de taper après un @, s'il y en a un. */
function motCourant(texte, position) {
  const avant = texte.slice(0, position);
  const arobase = avant.lastIndexOf("@");
  if (arobase === -1) return null;
  const morceau = avant.slice(arobase + 1);
  // Une identification ne traverse pas une ligne, et s'arrête au deuxième mot :
  // « @Grâce Adjovi » se cherche, « @Grâce a dit que » non.
  if (/[\n]/.test(morceau) || morceau.split(" ").length > 2) return null;
  // le @ doit commencer un mot, sinon une adresse e-mail ouvrirait la liste
  if (arobase > 0 && !/[\s(]/.test(avant[arobase - 1])) return null;
  return { debut: arobase, requete: morceau };
}

/**
 * Équipe un champ de saisie.
 *
 * @param {string} texte      la valeur courante du champ
 * @param {Function} setTexte pour la remplacer quand on choisit quelqu'un
 * @param {object} ctx
 */
export function useMentions(texte, setTexte, ctx) {
  const [propositions, setPropositions] = useState([]);
  const [cible, setCible] = useState(null); // { debut, requete }
  const choisis = useRef(new Map()); // nom exact -> uid
  const minuteur = useRef(null);

  const surSaisie = (champ) => {
    const trouve = motCourant(champ.value, champ.selectionStart);
    setCible(trouve);
    clearTimeout(minuteur.current);
    if (!trouve) return setPropositions([]);
    // Une frappe = une recherche serait une lecture par lettre : on attend.
    minuteur.current = setTimeout(async () => {
      const gens = (await Store.users.search(trouve.requete)).filter((u) => u.uid !== ctx.moi.uid);
      setPropositions(gens.slice(0, 5));
    }, 220);
  };

  const choisir = (u, champ) => {
    if (!cible) return;
    const avant = texte.slice(0, cible.debut);
    const apres = texte.slice(cible.debut + 1 + cible.requete.length);
    choisis.current.set(u.nom, u.uid);
    const nouveau = `${avant}@${u.nom} ${apres.replace(/^\s/, "")}`;
    setTexte(nouveau);
    setPropositions([]);
    setCible(null);
    // le curseur se replace juste après le nom, sinon on écrit au mauvais endroit
    requestAnimationFrame(() => {
      const p = (avant + "@" + u.nom + " ").length;
      champ?.focus();
      champ?.setSelectionRange(p, p);
    });
  };

  /** Ce qu'il faut enregistrer À CÔTÉ du texte — seulement ceux réellement cités. */
  const mentions = () =>
    [...choisis.current.entries()]
      .filter(([nom]) => texte.includes("@" + nom))
      .map(([nom, uid]) => ({ uid, nom }));

  const oublier = () => choisis.current.clear();

  return { propositions, surSaisie, choisir, mentions, oublier, actif: Boolean(cible) };
}

/** La petite liste qui s'ouvre sous le champ. */
export function ListeMentions({ propositions, onChoisir }) {
  if (!propositions.length) return null;
  return (
    <div className="mentions">
      {propositions.map((u) => (
        <button type="button" className="mentions__item" key={u.uid} onClick={() => onChoisir(u)}>
          <Avatar membre={u} taille={30} />
          <span>
            <strong>{u.nom}</strong>
            <span className="petit texte-doux">{u.eglise || u.ville || "Membre"}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
